/**
 * Reads all documents from Firestore collections 'oil_changes', 'truck_washes', and 'greasings',
 * then returns the truck maintenance data dictionary.
 * @param {object} db - Firestore database instance
 * @returns {Promise<Object>} Dictionary keyed by truck id (string), each with arrays: washes (dates), greasings (dates), oil change (mileages as integers)
 */
export async function fetchAllTruckMaintenanceData(db) {
  // Read all documents from each collection
  const [washesSnap, greasingsSnap, oilChangesSnap] = await Promise.all([
    db.collection('truck_washes').get(),
    db.collection('greasings').get(),
    db.collection('oil_changes').get()
  ]);

  // Map Firestore docs to plain objects
  const washes = washesSnap.docs.map(doc => ({
    truck: doc.data().truck,
    date: doc.data().date && doc.data().date.toDate ? doc.data().date.toDate() : doc.data().date
  }));

  const greasings = greasingsSnap.docs.map(doc => ({
    truck: doc.data().truck,
    date: doc.data().date && doc.data().date.toDate ? doc.data().date.toDate() : doc.data().date,
    mileage: doc.data().mileage
  }));

  const oilChanges = oilChangesSnap.docs.map(doc => ({
    truck: doc.data().truck,
    date: doc.data().date && doc.data().date.toDate ? doc.data().date.toDate() : doc.data().date,
    location: doc.data().location,
    mileage: doc.data().mileage,
    price: doc.data().price
  }));

  // Use the utility to build the dictionary
  return getTruckMaintenanceData(washes, greasings, oilChanges);
}

/**
 * Builds a dictionary of truck maintenance data from three collections.
 * @param {Array} washes - Array of { truck: string|number, date: Date }
 * @param {Array} greasings - Array of { truck: string|number, date: Date }
 * @param {Array} oilChanges - Array of { truck: string|number, date: Date, location: string, mileage: number, price: number }
 * @returns {Object} Dictionary keyed by truck id (string), each with arrays: washes (dates), greasings (dates), oil change (mileages as integers)
 */
export function getTruckMaintenanceData(washes, greasings, oilChanges) {
  const result = {};

  // Helper to ensure truck entry exists
  const ensureTruck = (truck) => {
    const truckId = String(truck);
    if (!result[truckId]) {
      result[truckId] = { washes: [], greasings: [], "oil change": [] };
    }
    return truckId;
  };

  // Populate washes
  washes.forEach(item => {
    const truckId = ensureTruck(item.truck);
    result[truckId].washes.push(item.date);
  });

  // Populate greasings
  greasings.forEach(item => {
    const truckId = ensureTruck(item.truck);
    result[truckId].greasings.push(item);
  });

  // Populate oil changes (only mileage as in the original structure)
  oilChanges.forEach(item => {
    const truckId = ensureTruck(item.truck);
    result[truckId]["oil change"].push(item);
  });

  return result;
}

/**
 * Reads all truck numbers from the 'trucks' Firestore collection.
 * @param {object} db - Firestore database instance
 * @returns {Promise<Array>} Resolves to an array of truck numbers (strings or numbers)
 */
export async function fetchTruckNumbers(db) {
  try {
    const trucksSnap = await db.collection('trucks').get();
    return trucksSnap.docs.map(doc => doc.data().truck);
  } catch (error) {
    alert('Error fetching truck numbers: ' + error.message);
    return [];
  }
}